import type { AiCompanionMode, SubjectEntityType } from './types';
import { subjectEntityTypes } from './types';

type ChatRedirectParams = {
  mode: AiCompanionMode;
  targetUrl: string;
  subject: { id: number; type: SubjectEntityType } | null;
};

const ALLOWED_URL_PREFIXES = [
  '/goals/',
  '/keyResults/',
  '/kpis/',
  '/initiatives/',
  '/conversations/',
  '/strategicElements/',
  '/analytics/',
] as const;

/** Securely validates an internal URL to prevent open redirects. */
export const isValidInternalUrl = (url: string): boolean => {
  if (!url.startsWith('/')) return false;

  // URL constructor normalizes path traversal (/../) and catches protocol-relative URLs (//evil.com)
  try {
    const parsed = new URL(url, 'https://placeholder.local');

    // Reject if origin changed (absolute URL was passed)
    if (parsed.origin !== 'https://placeholder.local') return false;

    return ALLOWED_URL_PREFIXES.some(prefix =>
      parsed.pathname.startsWith(prefix)
    );
  } catch {
    return false;
  }
};

export const isValidPositiveInt = (value: string): boolean => {
  const parsed = parseInt(value, 10);
  return !Number.isNaN(parsed) && parsed > 0 && String(parsed) === value;
};

const isValidSubjectType = (type: string): type is SubjectEntityType =>
  subjectEntityTypes.includes(type as SubjectEntityType);

export const parseQueryParams = (
  searchParams: URLSearchParams
): ChatRedirectParams => {
  const urlParam = searchParams.get('url');
  const subjectIdParam = searchParams.get('subjectId');
  const subjectTypeParam = searchParams.get('subjectType');

  let targetUrl = '/';
  let mode: AiCompanionMode = 'fullscreen';
  let subject: { id: number; type: SubjectEntityType } | null = null;

  if (urlParam && isValidInternalUrl(urlParam)) {
    targetUrl = urlParam;
    mode = 'sidebar';
  }

  if (
    subjectIdParam &&
    subjectTypeParam &&
    isValidPositiveInt(subjectIdParam) &&
    isValidSubjectType(subjectTypeParam)
  ) {
    subject = { id: parseInt(subjectIdParam, 10), type: subjectTypeParam };
  }

  return { mode, targetUrl, subject };
};
